import React from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import AtlasSales1P3PSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSales1P3PSplineChart';
import { useAppSelector } from 'src/utils/Hooks';
import { getAtlasProductTagsTreatment, shouldShowOnePRetailSales } from 'src/utils/app';
import {
  useBusinessUnitEntity,
  useBrandEntity,
  useProductEntity,
  useMainEntityType,
  useSegmentEntity,
  useCategoryEntity,
  useSubcategoryEntity,
  useTrendEntity
} from 'src/utils/Hooks/reduxSelectors';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import AtlasV2DorisSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisSplineChart';
import AtlasV2SalesMarketShareOfRetailSalesChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasV2SalesMarketShareOfRetailSalesChart';
import AtlasSalesProductCountSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesProductCountSplineChart';
import AtlasSales1PTotalSalesSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSales1PTotalSalesSplineChart';
import AtlasSalesDoubleDropdownTopEntityChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesDoubleDropdownTopEntityChart';
import { AtlasSalesV2ProductGrid } from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesV2ProductGrid';
import AtlasSalesMarketShareProductSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesMarketShareProductSplineChart';
import AtlasNon1PMarketShareSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasNon1PMarketShareSplineChart';
import AtlasSalesNon1PDropdownTopEntityChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesNon1PDropdownTopEntityChart';
import AtlasSalesMarketShareColumnChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesMarketShareColumnChart';
import AtlasMarketShareGridV2 from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasMarketShareGridV2';
import AtlasSalesNon1PMarketShareColumnChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesNon1PMarketShareColumnChart';
import { useAtlasV2TabTreatmentRedirect } from 'src/components/AtlasV2Components/Hooks/useAtlasV2TabTreatmentRedirect';

export const ONE_P_THRESHOLD = 25;
/**
 * Atlas Sales Summary V2 Page layout - Tab name: sales subtab name: retailSales
 */
export const AtlasSalesSummaryV2PageLayout = () => {
  const skuCount = useAppSelector((state) => state.entityService.mainEntity.skuCount);
  const mainEntityType = useMainEntityType();
  const { isSegmentEntity } = useSegmentEntity();
  const { isProductEntity } = useProductEntity();
  const { isCategoryEntity, isAllCategoriesEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const { isBrandEntity } = useBrandEntity();
  const { isTrendEntity, trendEntity } = useTrendEntity();
  const { isRedirecting } = useAtlasV2TabTreatmentRedirect();

  const productTagsEnabledCondition =
    isSegmentEntity ||
    isBrandEntity ||
    isBusinessUnitEntity ||
    isSubcategoryEntity ||
    (isCategoryEntity && !isAllCategoriesEntity);

  if (isRedirecting) return null;

  // for segment, if sku count is less than or equal to 25, hide the one P, three P spline chart
  return shouldShowOnePRetailSales() && !(isSegmentEntity && skuCount <= ONE_P_THRESHOLD) && !isTrendEntity ? (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        {isProductEntity ? (
          <AtlasV2DorisSplineChart
            queryKey="RetailSalesSplineChart"
            title="Retail Sales"
            methodName="sales/GetSalesMetrics"
            metricTypeDisplay={METRICTYPE.MONEY}
            metricType="RetailSales"
          />
        ) : (
          <AtlasSales1P3PSplineChart />
        )}

        {(mainEntityType === 'company' || isBrandEntity || isTrendEntity) && (
          <AtlasV2SalesMarketShareOfRetailSalesChart />
        )}

        {!isProductEntity && <AtlasSalesProductCountSplineChart />}

        {mainEntityType === 'company' && <AtlasSales1PTotalSalesSplineChart />}

        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity || isTrendEntity) && (
          <AtlasSalesMarketShareColumnChart enableSearchableGroupBy={false} />
        )}
        {mainEntityType === 'company' && <AtlasSalesDoubleDropdownTopEntityChart />}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand')) && <AtlasMarketShareGridV2 metricType="retailSales" />}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          isTrendEntity) && (
          //  Double Dropdown Retail Sales, 1P, 3P by Brand Manufacturer
          <AtlasSalesDoubleDropdownTopEntityChart
            enableSearchableGroupBy={productTagsEnabledCondition && getAtlasProductTagsTreatment() === 'on'}
          />
        )}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          isTrendEntity) && (
          //  Double Dropdown Retail Sales, 1P, 3P by Brand Manufacturer
          <AtlasSales1PTotalSalesSplineChart />
        )}

        {isProductEntity && <AtlasSalesMarketShareProductSplineChart />}

        <AtlasSalesV2ProductGrid salesType="retailSales" />
      </Flex>
    </BasePageContainer>
  ) : (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        <AtlasV2DorisSplineChart
          queryKey="RetailSalesSplineChart"
          title="Retail Sales"
          methodName="sales/GetSalesMetrics"
          metricTypeDisplay={METRICTYPE.MONEY}
          metricType="RetailSales"
        />
        {(mainEntityType === 'company' || isBrandEntity || isProductEntity || isTrendEntity) && (
          <AtlasNon1PMarketShareSplineChart />
        )}

        {!isProductEntity && <AtlasSalesProductCountSplineChart />}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand')) && <AtlasSalesNon1PMarketShareColumnChart />}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand') ||
          isSegmentEntity ||
          mainEntityType === 'businessunit') && <AtlasMarketShareGridV2 metricType="retailSales" />}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand')) && (
          <AtlasSalesNon1PDropdownTopEntityChart
            enableSearchableGroupBy={productTagsEnabledCondition && getAtlasProductTagsTreatment() === 'on'}
          />
        )}

        <AtlasSalesV2ProductGrid salesType="retailSales" />
      </Flex>
    </BasePageContainer>
  );
};
